import React, { useEffect, useState } from 'react';
import SubtleLoader from './SubtleLoader';

interface DebouncedLoaderProps {
    loading: boolean;
    delay?: number;
    size?: number;
    sx?: any;
    loader?: React.ReactNode;
    centered?: boolean;
    visible?: boolean;
    children?: React.ReactNode;
}

/**
 * DebouncedLoader component delays the display of a loader to avoid flickering when the loading
 * time is short.
 */
const DebouncedLoader: React.FC<DebouncedLoaderProps> = ({
    loading,
    delay = 2000,
    size = 24,
    sx = {},
    loader = <SubtleLoader size={size} sx={sx} />,
    centered = false,
    visible = true,
    children,
}) => {
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;

        if (loading) {
            timeout = setTimeout(() => {
                setShowLoader(true);
            }, delay);
        } else {
            setShowLoader(false);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [loading, delay]);

    if (loading && showLoader && visible) {
        const Wrapper = centered ? 'div' : React.Fragment;
        const wrapperProps = centered
            ? {
                  style: {
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      marginTop: '2rem',
                      marginBottom: '2rem',
                  },
              }
            : {};

        return <Wrapper {...wrapperProps}>{loader}</Wrapper>;
    }

    if (!loading) {
        return <>{children}</>;
    }

    return null;
};

export default DebouncedLoader;
